import { Eye, GitCompareArrows, Hammer, RotateCcw, Trash2, Undo2 } from 'lucide-react'

import type { Historial, VersionEnHistorial } from '../../api/tipos'
import { Boton, Pastilla, Tarjeta, clases } from '../../componentes/ui'
import { estadoDeVersion, quienEs, resumenDeCambios } from './textos'

type Bloque = { slug: string; etiqueta: string }

const fecha = (iso: null | string) => (iso ? new Date(iso).toLocaleString('es', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '')

export type AccionesDelHistorial = {
  previa: (numero: number) => void
  comparar: (v: VersionEnHistorial) => void
  restaurar: (v: VersionEnHistorial) => void
  descartar: (v: VersionEnHistorial) => void
  construir: (v: VersionEnHistorial) => void
  volverALaAnterior: () => void
}

/**
 * El historial del diseño (CLAUDE.md §12, §20), con lo que se puede hacer con cada versión
 * **según lo que dice el núcleo**: la previa solo si está construida, descartar lo que no está
 * publicado (también lo que no construyó), construir lo que se quedó sin preparar, y volver a
 * la versión anterior si quien mira puede publicar. Nada técnico: ni números de artefacto ni
 * rutas de ficheros; «versión 12» es lo único que se numera, porque es lo que se compara.
 */
export const HistorialDeDiseno = ({
  historial,
  bloques,
  versionActual,
  ocupado,
  congelado,
  acciones,
}: {
  historial: Historial
  bloques: Bloque[]
  versionActual: null | number
  ocupado: boolean
  congelado: boolean
  acciones: AccionesDelHistorial
}) => {
  const { versiones, permiso, puedePublicar, anterior, publicada } = historial
  const mira = permiso !== 'ninguno'
  const cambia = mira && !congelado
  const restaura = cambia && (permiso === 'equipo' || permiso === 'publicar')
  return (
    <section aria-labelledby="historial-titulo">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-lg font-semibold" id="historial-titulo">
          Historial de versiones
        </h2>
        {puedePublicar && publicada > 0 && anterior && (
          <Boton disabled={ocupado || congelado} icono={<Undo2 />} onClick={acciones.volverALaAnterior} pequeno>
            Volver a la versión anterior
          </Boton>
        )}
      </div>
      {!versiones.length ? (
        <p className="text-sm text-tenue">Todavía no hay versiones. La primera la crea el equipo, o la generas tú ahora mismo.</p>
      ) : (
        <Tarjeta className="overflow-x-auto" sinRelleno>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-linea bg-papel-2 text-left text-xs text-tenue">
                <th className="px-4 py-2.5 font-medium">Versión</th>
                <th className="px-4 py-2.5 font-medium">Quién</th>
                <th className="px-4 py-2.5 font-medium">Qué</th>
                <th className="px-4 py-2.5 font-medium">Estado</th>
                <th className="px-4 py-2.5 font-medium">Cuándo</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {versiones.map((v) => {
                const estado = estadoDeVersion(v)
                const enMarcha = v.estado === 'en-cola' || v.estado === 'construyendo'
                const cambios = resumenDeCambios(v.cambios, bloques)
                return (
                  <tr className={clases('border-b border-linea align-top last:border-b-0', v.numero === versionActual && 'bg-violeta-suave/40', v.descartada && 'text-tenue')} data-version={v.numero} key={v.numero}>
                    <td className="px-4 py-3 font-medium whitespace-nowrap">{v.numero}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{quienEs(v)}</td>
                    <td className="max-w-md px-4 py-3 text-tinta-2">
                      <span className="line-clamp-2" title={v.prompt ?? v.comentario}>
                        {v.comentario}
                      </span>
                      {cambios && <span className="mt-0.5 block truncate text-xs text-tenue" title={cambios}>{cambios}</span>}
                      {v.solicitud?.estado === 'rechazada' && v.solicitud.comentario && <span className="mt-1 block text-xs text-error">El equipo: {v.solicitud.comentario}</span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Pastilla clave={estado.clave}>{estado.texto}</Pastilla>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-tenue tabular-nums">{fecha(v.fecha)}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap justify-end gap-1">
                        {mira && v.construida && (
                          <Boton disabled={ocupado} icono={<Eye />} onClick={() => acciones.previa(v.numero)} pequeno tipo="discreto">
                            Previa
                          </Boton>
                        )}
                        <Boton disabled={ocupado} icono={<GitCompareArrows />} onClick={() => acciones.comparar(v)} pequeno tipo="discreto">
                          Comparar
                        </Boton>
                        {cambia && v.estado === 'borrador' && !v.descartada && (
                          <Boton disabled={ocupado} icono={<Hammer />} onClick={() => acciones.construir(v)} pequeno tipo="discreto">
                            Preparar la previa
                          </Boton>
                        )}
                        {restaura && !v.publicada && v.construida && (
                          <Boton disabled={ocupado} icono={<RotateCcw />} onClick={() => acciones.restaurar(v)} pequeno tipo="discreto">
                            Restaurar
                          </Boton>
                        )}
                        {cambia && !v.publicada && !v.descartada && !enMarcha && (
                          <Boton disabled={ocupado} icono={<Trash2 />} onClick={() => acciones.descartar(v)} pequeno tipo="discreto">
                            Descartar
                          </Boton>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </Tarjeta>
      )}
    </section>
  )
}
